import React, { useState, useEffect } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import CurrentUser from "../apiResources/CurrentUser";
import EmployerUser from "../apiResources/EmployerUser";
import BasePage from "./BasePage";
import Alert from "./library/Alert";
import NameSettingOption from "./settings/NameSettingOption";
import EmailSettingOption from "./settings/EmailSettingOption";
import MobileNumberSettingOption from "./settings/MobileNumberSettingOption";
import PasswordSettingOption from "./settings/PasswordSettingOption";
import JobTitleSettingOption from "./settings/JobTitleSettingOption";
import BiographySettingOption from "./settings/BiographySettingOption";
import LinkedInSettingOption from "./settings/LinkedInSettingOption";
import TwitterSettingOption from "./settings/TwitterSettingOption";
import PhotoSettingOption from "./settings/PhotoSettingOption";
import Divider from "./library/Divider";
import Container from "./library/Container";

const EmployerUserSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>(null);
  const [user, setUser] = useState<EmployerUser>(null);
  const [editFocus, setEditFocus] = useState<string>(null);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const { id } = (await CurrentUser.first()).data;
        const { data } = await EmployerUser.find(id);
        setUser(data);
      } catch (err) {
        Sentry.captureException(err);
        setError("Something went wrong trying to load your user info");
      } finally {
        setLoading(false);
      }
    };

    void fetchUser();
  }, []);

  const resetEditFocus = () => setEditFocus(null);

  const isEditing = (key) => Boolean(editFocus) && editFocus === key;
  const isDisabled = (key) => Boolean(editFocus) && editFocus !== key;

  return (
    <BasePage title="Your Account">
      <Container>
        {error && <Alert status="error">{error}</Alert>}

        <OptionsList>
          <NameSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("name")}
            disabled={isDisabled("name")}
            onEdit={() => setEditFocus("name")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <EmailSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("email")}
            disabled={isDisabled("email")}
            onEdit={() => setEditFocus("email")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <PhotoSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("photo")}
            disabled={isDisabled("photo")}
            onEdit={() => setEditFocus("photo")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <PasswordSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("password")}
            disabled={isDisabled("password")}
            onEdit={() => setEditFocus("password")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <MobileNumberSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("mobileNumber")}
            disabled={isDisabled("mobileNumber")}
            onEdit={() => setEditFocus("mobileNumber")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <JobTitleSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("jobTitle")}
            disabled={isDisabled("jobTitle")}
            onEdit={() => setEditFocus("jobTitle")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <LinkedInSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("linkedIn")}
            disabled={isDisabled("linkedIn")}
            onEdit={() => setEditFocus("linkedIn")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <TwitterSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("twitter")}
            disabled={isDisabled("twitter")}
            onEdit={() => setEditFocus("twitter")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <BiographySettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("biography")}
            disabled={isDisabled("biography")}
            onEdit={() => setEditFocus("biography")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
        </OptionsList>
      </Container>
    </BasePage>
  );
};

const OptionsList = styled.div`
  max-width: 100%;
`;

export default EmployerUserSettings;
